import { motion } from 'framer-motion'
import { X, CreditCard, User, DollarSign } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatCurrency } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

interface QRConfirmModalProps {
  qrData: {
    type: string
    amount: string | number
    currency?: string
    user_name?: string
    name?: string
    display_name?: string
    merchant_name?: string
    description?: string
    wallet_id?: string
    qr_hash?: string
  }
  onClose: () => void
  onConfirm: () => void
  isProcessing?: boolean
}

export function QRConfirmModal({ qrData, onClose, onConfirm, isProcessing = false }: QRConfirmModalProps) {
  const { t } = useTranslation()
  
  // Get the name to display (user_name comes from backend lookup)
  const displayName = qrData.user_name || qrData.name || qrData.display_name || qrData.merchant_name || t('qrScanner.noUserInfo')
  
  // Get the amount and currency
  const amount = typeof qrData.amount === 'string' ? parseFloat(qrData.amount) : qrData.amount
  const currency = qrData.currency || 'STN'
  const formattedAmount = formatCurrency(amount, currency)
  
  // Format the message with interpolation - ensure values are replaced
  let message = t('qrScanner.confirmPaymentMessage', {
    amount: formattedAmount,
    name: displayName
  })
  
  // Fallback: if interpolation didn't work, manually replace
  if (message.includes('{amount}') || message.includes('{name}')) {
    message = message.replace('{amount}', formattedAmount).replace('{name}', displayName)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 pb-20 md:pb-4"
      onClick={!isProcessing ? onClose : undefined}
    >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white rounded-2xl w-full max-w-md max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100">
            <h2 className="text-xl font-bold text-text-primary">
              {t('qrScanner.confirmPayment')}
            </h2>
            {!isProcessing && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="h-5 w-5 text-text-secondary" />
              </button>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Processing state */}
            {isProcessing ? (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <CreditCard className="h-8 w-8 text-primary" />
                  </motion.div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">
                    {t('qrScanner.processingPayment')}
                  </h3>
                  <p className="text-text-secondary text-sm">
                    {message}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Amount display */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="h-8 w-8 text-primary" />
                  </div>
                  <p className="text-3xl font-bold text-text-primary mb-2">
                    {formatCurrency(amount, currency)}
                  </p>
                  <div className="flex items-center justify-center gap-2 text-text-secondary mb-4">
                    <User className="h-4 w-4" />
                    <p className="text-sm font-medium">{displayName}</p>
                  </div>
                  {qrData.description && (
                    <p className="text-text-secondary text-sm italic">
                      "{qrData.description}"
                    </p>
                  )}
                </div>

                {/* Message */}
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-text-primary font-medium">
                    {message}
                  </p>
                </div>

                {/* Action buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={onConfirm}
                    className="w-full"
                  >
                    {t('common.yes')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="w-full"
                  >
                    {t('common.no')}
                  </Button>
                </div>
              </div>
            )}
          </div>
      </motion.div>
    </motion.div>
  )
}

